import Backbone from 'backbone';
import ko from 'knockout';
import _ from 'underscore';
import Promise from 'bluebird';
import { withUiBlock } from '@bingads-webui/uiblocker';
import { LocationRecommendationGrid } from '@bingads-webui-campaign/location-recommendation-grid';
import inlineTemplate from './inline-template.pug';
import locationGridAccountTemplate from './location-grid-account-template.pug';
import locationGridCampaignTemplate from './location-grid-campaign-template.pug';
import locationGridTemplate from './location-grid-template.pug';
import Model from './model';

// Start your code from here for component inline-location-recommendation
const recommendationType = 'NewLocationTargetOpportunity';
const channel = 'campaignsetting';
const templates = {
  inlineTemplate,
  locationGridAccountTemplate,
  locationGridCampaignTemplate,
};

function logViewEvent(deps, opportunitys) {
  const { logger, constants } = deps;
  _.each(opportunitys, ({ OpportunityId: id, Campaign = {} }) => {
    logger.logMessage(
      [constants.LOGCATEGORY.TRACE],
      constants.LOGLEVEL.INFO,
      'Recommendations',
      {
        type: recommendationType,
        typeId: 216, // opportunity type id, correspond to recommendation type https://msasg.visualstudio.com/Bing_Ads/_git/AdInsights?path=%2Fprivate%2FMTv2%2FPlugins%2FOpportunity%2FSchema%2FOpportunityType.cs&version=GBmaster
        id,
        campaignId: Campaign.Id,
        isClick: false,
        channel,
      }
    );
  });
}

export class InlineLocationRecommendation extends Backbone.View {
  initialize(options) {
    this.options = options;
    this.deps = options.deps;
    this.dataSource = options.dataSource;
    this.isAllSelected = false;
    this.i18n = this.deps.i18n;
    this.model = new Model({
      i18n: this.i18n,
      scope: options.scope,
      isExpanded: !!options.isExpanded,
    });
    this.alertHelper = new this.deps.AlertHelper({ i18n: this.i18n });
    this.isAccountLevel = !_.isFinite(options.scope.campaignId);
    const columns = this.isAccountLevel ? [
      'Campaign',
      'Location',
      'Impression',
      'Dismiss',
    ] : ['Location',
      'Impression',
      'Dismiss',
    ];
    this.locationRecommendationGrid = new LocationRecommendationGrid({
      columns,
      dataSource: {
        type: 'memory',
        data: [],
      },
      hasPagination: this.isAccountLevel,
      selectAllByDefault: !this.isAccountLevel,
      ...options,
    }).render();
    this.locationRecommendationGrid.on('selectChanged', selectedItems => this.model.selectedItems(selectedItems));
    this.locationRecommendationGrid.on('isAllSelected', (isAllSelected) => { this.isAllSelected = isAllSelected; });
    this.locationRecommendationGrid.on('dismiss', recommendation => this.dismissRecommendations([recommendation]));
    this.locationRecommendationGrid.on('mouseover:recommendation', recommendation => this.mouseOverRecommendation(recommendation));
    this.locationRecommendationGrid.on('mouseout:recommendation', recommendation => this.mouseOutRecommendation(recommendation));
    this.displayedRecommendations = [];
    this.locationRecommendationGrid.on('display:recommendations', (recommendations) => {
      if (this.model.isExpanded()) {
        logViewEvent(this.deps, recommendations);
        return;
      }
      this.displayedRecommendations = recommendations;
    });

    const isMapLoaded = window.Microsoft && window.Microsoft.Maps && window.Microsoft.Maps.loadModule;
    this.showMap = isMapLoaded && ((!this.isAccountLevel && this.options.isCampaignLevelLocation)
      || _.isFinite(options.scope.adgroupId));
    if (this.showMap) {
      const MapView = this.deps.LocationMap;
      this.mapView = new MapView({
        disableInfoBoxGroupList: ['included', 'excluded'],
        mapHeight: this.options.mapHeight,
      }).render();

      this.mapView.on('target:recommendation', recommendation => this.targetRecommendations([recommendation]));
      this.mapView.on('dismiss:recommendation', recommendation => this.dismissRecommendations([recommendation]));

      this.model.locationRecommendations.subscribe((recommendations) => {
        if (this.model.isExpanded() || this.options.showRecommendationsOnMap) {
          this.updateRecommendedLocations(recommendations);
        }
      });

      if (!this.options.showRecommendationsOnMap) {
        if (!this.model.isMapShow()) {
          this.mapView.$el.hide();
        }

        this.model.isMapShow.subscribe((show) => {
          if (show) {
            this.mapView.$el.show();
          } else {
            this.mapView.$el.hide();
          }
        });
      }
    }
    this.model.locationRecommendations.subscribe((recommendations) => {
      this.trigger('update:recommendations', recommendations);
    });
    this.model.isExpanded.subscribe((isExpanded) => {
      const recommendations = isExpanded ? this.model.locationRecommendations() : [];
      if (isExpanded) {
        this.trigger('expand:collapse');
        if (!_.isEmpty(this.displayedRecommendations)) {
          logViewEvent(this.deps, this.displayedRecommendations);
          this.displayedRecommendations = [];
        }
      } else {
        this.trigger('close:collapse');
      }
      this.updateRecommendedLocations(recommendations);
    });

    this.template = locationGridTemplate;
    if (options.template) {
      this.template = _.isString(options.template) ? templates[options.template] : options.template;
    }
    this.on('refresh', () => {
      this.refreshLocation();
    });
  }

  get events() {
    return {
      'click .apply': 'apply',
      'click .cancel': 'cancel',
      'click .dismiss': 'dismiss',
    };
  }

  updateRecommendedLocations(recommendations) {
    this.refreshMap();
    if (this.mapView) {
      this.mapView.updateRecommendedLocations(recommendations);
      this.model.recommendationsOnMap(recommendations);
    }
  }

  refreshMap() {
    if (this.mapView) {
      this.mapView.rebindEventBus();
    }
  }

  refreshLocation() {
    const {
      isCampaignLevelLocation,
    } = this.options;
    if (isCampaignLevelLocation) { // only have recommendation on campaign level
      this.model.selectedItems([]);
      this.fetchRecommendations().finally(withUiBlock(this.$el));
    }
    if (this.showMap) {
      this.loadLocationCriterion();
    }
  }

  loadLocationCriterion() {
    const {
      scope: {
        campaignId,
        adgroupId,
      },
      excludedOnly,
    } = this.options;
    if (_.isUndefined(campaignId)) {
      return;
    }
    const schema = _.isFinite(adgroupId) ? this.deps.s$AdGroupCriterion :
      this.deps.s$CampaignCriterion;
    const criterionLevel = _.isFinite(adgroupId) ? 'adgroup' : 'campaign';
    const parentId = adgroupId || campaignId;
    this.deps.criterionLoader.load(campaignId, adgroupId, ['LocationCriterion', 'RadiusCriterion', 'LocationIntentCriterion'], schema)
      .then((criterions) => {
        let entities = _.chain(criterions)
          .filter(criterion =>
            criterion.Criterion.Type === 'Radius' || criterion.Criterion.Type === 'Location')
          .map(criterion =>
            this.deps.locationTargetConverter.buildLocationCriterionFromBiddableCampaignCriterion(
              criterion,
              criterionLevel,
              parentId
            ))
          .value();
        if (excludedOnly) {
          entities = entities.filter(entity => entity.SelectionStatus === 'Excluded');
        }
        this.model.criterions(entities);
        if (this.mapView) {
          this.mapView.waitUntilMapLoaded().then(() => {
            this.refreshMap();
            this.deps.jsDataCampaign.Intermediate.refresh('CampaignCriterion', entities, { destroyAll: true });
          });
        }
      });
  }

  fetchRecommendations() {
    return this.deps.recommendationRepository.getRecommendations({
      deps: this.deps,
      oppType: recommendationType,
      scope: this.options.scope,
      channel,
      top: this.options.scope.campaignId ? 5 : 500, // campaign level: 5; account level: 500
      skip: 0, // pagination on client side
    }).then((data) => {
      this.model.locationRecommendations(data.value);
      this.locationRecommendationGrid.trigger('update:data', data.value);
    }).catch(() => {
      this.model.locationRecommendations([]);
      this.locationRecommendationGrid.trigger('update:data', []);
    });
  }

  mouseOverRecommendation(recommendation) {
    const { Id } = recommendation || {};

    if (this.showMap && _.isFinite(Id)) {
      this.mapView.trigger('entity:highlight', Id);
    }
  }

  mouseOutRecommendation(recommendation) {
    const { Id } = recommendation || {};

    if (this.showMap && _.isFinite(Id)) {
      this.mapView.trigger('entity:reset', Id);
    }
  }

  targetRecommendations(recommends) {
    const recommendations = this.isAllSelected ? this.model.locationRecommendations() : recommends;
    const reqs = _.chain(recommendations)
      .map(recommend => _.pick(recommend, _.keys(this.deps.s$CampaignCriterion.properties)))
      .groupBy(recommend => recommend.Campaign.Id)
      .map(values => ({
        Id: values[0] && values[0].Campaign.Id,
        data: {
          CampaignCriterions: _.map(values, val => ({
            Campaign: _.pick(val.Campaign, 'Id'),
            SelectionStatus: 'Included',
            ..._.pick(val, ['Criterion', 'CriterionType']),
          })),
        },
      }))
      .value();
    let appliedRecommendations = recommendations;

    return Promise.map(reqs, req =>
      this.deps.jsDataCampaign.Campaign.update(req.Id, { reqs: [req] }, {
        adapter: 'bulk', cacheResponse: false, overrideResponse: true, batch: true,
      })).catch((e) => {
      // Failure as location already exists will be marked as success
      const failedCriterions = _.filter(e.entity.CampaignCriterions, criterion =>
        !!_.find(e.errors, error => error['Client Id'] === criterion['Client Id'] && error.ErrorNumber !== '1043'));

      appliedRecommendations = _.reject(recommendations, recommend =>
        !!_.find(failedCriterions, ({ Criterion }) => _.isEqual(Criterion, recommend.Criterion)));
    }).finally(() => {
      if (_.size(appliedRecommendations) > 0) {
        this.deps.recommendationRepository.applyRecommendations({
          deps: this.deps,
          recommendations: appliedRecommendations,
          recommendationType,
          scope: this.options.scope,
          isAll: this.isAllSelected &&
            _.size(recommendations) > 1 &&
            _.size(appliedRecommendations) === _.size(recommendations) &&
            !this.options.scope.campaignId,
          channel,
        }).then(() => {
          /*
            After sync with ad insight MT,
            even apply failed, ad insight still can track the recommendations,
            so we don't need to parse the response
          */
          this.alertHelper.showSuccess(this.i18n.getString('Apply_Location_recommendations_Success', {
            success: _.size(appliedRecommendations),
            total: _.size(recommendations),
          }));
        }).finally(() => {
          this.trigger('add:locations', appliedRecommendations);
          this.refreshLocation();
          this.trigger('operate:recommendations');
        }).finally(withUiBlock(this.$el));
      } else if (_.size(appliedRecommendations) === 0 &&
        _.size(recommendations) > 0) { // no recommendations target success
        this.alertHelper.showError(this.i18n.getString('Apply_Location_recommendations_Failed'));
      }
    });
  }

  dismissRecommendations(recommendations) {
    return this.deps.recommendationRepository.dismissRecommendations({
      deps: this.deps,
      recommendations,
      recommendationType,
      scope: this.options.scope,
      isAll: this.isAllSelected && _.size(recommendations) > 1 && !this.options.scope.campaignId,
      channel,
    }).then((data) => {
      const ret = {
        failed: 0,
        success: 0,
        ..._.countBy(data.Results, result => (result.ErrorCode > 0 ? 'failed' : 'success')),
      };
      this.alertHelper.showSuccess(this.i18n.getString('Dismiss_Location_recommendations_Success', {
        success: ret.success,
        total: _.size(recommendations),
      }));
      this.deps.CampaignCacheManager.invalidate('Campaign');
      this.refreshLocation();
      this.trigger('operate:recommendations');
    }).catch(() => {
      this.alertHelper.showError(this.i18n.getString('Dismiss_Location_recommendations_Failed'));
    }).finally(withUiBlock(this.$el));
  }

  apply() {
    const selectedItems = this.model.selectedItems();
    if (_.isEmpty(selectedItems)) {
      return;
    }
    this.targetRecommendations(this.model.selectedItems());
  }

  cancel(e) {
    this.trigger('cancel', e);
  }

  dismiss() {
    const selectedItems = this.model.selectedItems();
    if (_.isEmpty(selectedItems)) {
      return;
    }
    this.dismissRecommendations(this.model.selectedItems());
  }

  remove() {
    this.trigger('remove');
    if (this.mapView) {
      this.mapView.remove();
    }
    if (this.locationRecommendationGrid) {
      this.locationRecommendationGrid.remove();
    }
    super.remove();
  }

  expandCollapse() {
    const key = 'expandRecommendLocationsByDefault';
    const expandRecommendLocations = this.deps.sessionStorage.get(key);
    if (!expandRecommendLocations) {
      return;
    }
    this.deps.sessionStorage.invalidate(key);
    const $collpase = this.$('.alert-recommendation .alert-info-inline');
    if ($collpase.length > 0) {
      $collpase.click();
    }
  }

  render() {
    this.$el.html(this.template({
      i18n: this.i18n,
      showMap: this.showMap,
    }));
    ko.applyBindings(this.model, this.el);
    this.$('.location-recommendation-grid-container').append(this.locationRecommendationGrid.el);
    if (this.showMap) {
      this.$('.location-recommendation-map-container').append(this.mapView.el);
    }
    _.delay(() => this.refreshLocation(), 100);
    _.delay(() => this.expandCollapse(), 200);
    return this;
  }
}
